import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { verifyAdminAccess } from './adminService';
import { logAdminActivity } from '../firebase/helpers';

const WITHDRAWAL_LIMIT_CONFIG_COLLECTION = 'withdrawalLimits';
const WITHDRAWAL_LIMIT_CONFIG_DOC_ID = 'config';

/**
 * Default withdrawal limits
 */
const DEFAULT_LIMITS = {
  minAmount: 100, // Minimum withdrawal amount in USDT (fixed at $100)
  maxAmount: 10000, // Maximum withdrawal amount in USDT (configurable)
  maxDailyAmount: 10000, // Maximum daily withdrawal per user
  updatedAt: null,
  updatedBy: null,
  updatedByEmail: null
};

/**
 * Get withdrawal limit configuration
 * Returns default values if not set in database
 */
export const getWithdrawalLimits = async () => {
  try {
    const configRef = doc(db, WITHDRAWAL_LIMIT_CONFIG_COLLECTION, WITHDRAWAL_LIMIT_CONFIG_DOC_ID);
    const snapshot = await getDoc(configRef);
    
    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        minAmount: data.minAmount || DEFAULT_LIMITS.minAmount,
        maxAmount: data.maxAmount || DEFAULT_LIMITS.maxAmount,
        maxDailyAmount: data.maxDailyAmount || DEFAULT_LIMITS.maxDailyAmount,
        updatedAt: data.updatedAt,
        updatedBy: data.updatedBy,
        updatedByEmail: data.updatedByEmail
      };
    }
    
    // Return defaults if not configured
    return DEFAULT_LIMITS;
  } catch (error) {
    console.error('Error getting withdrawal limits:', error);
    // Return defaults on error
    return DEFAULT_LIMITS;
  }
};

/**
 * Update withdrawal limit configuration (Admin only)
 */
export const updateWithdrawalLimits = async (limits) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    // Validate limits
    if (limits.minAmount !== undefined && limits.minAmount < 100) {
      throw new Error('Minimum withdrawal amount cannot be less than $100');
    }
    
    if (limits.maxAmount !== undefined && limits.maxAmount < 100) {
      throw new Error('Maximum withdrawal amount cannot be less than $100');
    }
    
    if (limits.minAmount !== undefined && limits.maxAmount !== undefined && limits.minAmount > limits.maxAmount) {
      throw new Error('Minimum withdrawal amount cannot be greater than maximum withdrawal amount');
    }
    
    // Get current limits to track changes
    const currentLimits = await getWithdrawalLimits();
    
    // Prepare update data
    const updateData = {
      // minAmount is fixed at 100, but we'll still allow it to be set for consistency
      minAmount: limits.minAmount !== undefined ? limits.minAmount : currentLimits.minAmount,
      maxAmount: limits.maxAmount !== undefined ? limits.maxAmount : currentLimits.maxAmount,
      maxDailyAmount: limits.maxDailyAmount !== undefined ? limits.maxDailyAmount : currentLimits.maxDailyAmount,
      updatedAt: Timestamp.now(),
      updatedBy: adminInfo.adminId,
      updatedByEmail: adminInfo.adminEmail
    };
    
    // Ensure minAmount is at least 100
    if (updateData.minAmount < 100) {
      updateData.minAmount = 100;
    }
    
    // Save to database
    const configRef = doc(db, WITHDRAWAL_LIMIT_CONFIG_COLLECTION, WITHDRAWAL_LIMIT_CONFIG_DOC_ID);
    await setDoc(configRef, updateData, { merge: true });
    
    // Log admin activity
    const changes = {};
    if (limits.minAmount !== undefined && limits.minAmount !== currentLimits.minAmount) {
      changes.minAmount = { from: currentLimits.minAmount, to: limits.minAmount };
    }
    if (limits.maxAmount !== undefined && limits.maxAmount !== currentLimits.maxAmount) {
      changes.maxAmount = { from: currentLimits.maxAmount, to: limits.maxAmount };
    }
    if (limits.maxDailyAmount !== undefined && limits.maxDailyAmount !== currentLimits.maxDailyAmount) {
      changes.maxDailyAmount = { from: currentLimits.maxDailyAmount, to: limits.maxDailyAmount };
    }
    
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'updateWithdrawalLimits',
      targetUserId: null,
      targetUserEmail: null,
      changes: changes,
      description: 'Updated withdrawal limit configuration'
    });
    
    return updateData;
  } catch (error) {
    console.error('Error updating withdrawal limits:', error);
    throw error;
  }
};

